import React, { useMemo } from 'react';
import last from 'lodash/last';

import {
  generateChartLabelsForTimeRange,
  getChartDropdownOptions,
  getPipelineChartLink,
} from '../../../utils/chart_utils/chart_utils';

import useCSVChartData from '../helpers/useCSVChartData';
import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query';

import ChartCard from '../ChartCard/ChartCard';
import { ChartProvider } from '../ChartContext';
import MultiAverageDetailPane from '../details/MultiAverageDetailPane';

import { formatCSVDate, formatChartDate } from '../../../utils/date_utils';

import { dataArrayAsDataSet, formatSingleLineChart } from '../helpers/dataset.helpers';

export const useAnomalyCountData = ({
  pipelineID,
  startTimestamp,
  endTimestamp,
  step
}) => {
  const prometheusResponse = usePrometheusQuery(
    pipelineID === null ? null : `sum(anomaly_counter{pipeline_name="${pipelineID}"})`,
    {
      startTimestamp,
      endTimestamp,
      step,
    },
  );

  const chartLabels = useMemo(() => generateChartLabelsForTimeRange(startTimestamp, endTimestamp, step), [startTimestamp, endTimestamp, step]);

  return {
    labels: chartLabels,
    chartData: prometheusResponse?.data,
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const AnomalyCountChart = ({
  chartData: rawChartData,
  labels,
  labelFormatString,
  message,
  pipelineID,
  pipelineVersion,
  chartID,
  isLoading = false,
  isEnlarged = false,
}) => {
  const chartTitle = 'Anomaly count';

  const chartLink = useMemo(() => getPipelineChartLink({
    pipelineID, pipelineVersion, chartID,
  }), [chartID, pipelineID, pipelineVersion]);

  const chartData = rawChartData?.map(last);
  const chartDataset = dataArrayAsDataSet(rawChartData?.map(last), chartTitle);

  const csvData = useCSVChartData({
    chartType: 'simple',
    title: chartTitle,
    data: chartData?.map(last),
    labels: labels?.map(formatCSVDate),
  })

  const dropdownOptions = getChartDropdownOptions({
    chartLink,
    chartTitle,
    csvData,
    pipelineID,
    pipelineVersion,
    isEnlarged,
  })

  return (
    <ChartProvider
      chartID={chartID}
      chartType="line"
      chartConfiguration="line"
      datasets={[chartDataset]}
      labels={labels}
      dropdownOptions={dropdownOptions}
      prepareDataset={formatSingleLineChart}
    >
      <ChartCard
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        detailRenderer={MultiAverageDetailPane}
        formatXLabel={formatChartDate(labelFormatString)}
      />
    </ChartProvider>
  )
}

const AnomalyCountChartWithData = (props) => {
  const {
    pipelineID, startTimestamp, endTimestamp, step
  } = props;

  const {
    chartData,
    labels,
    isLoading,
    message,
  } = useAnomalyCountData({
    pipelineID, startTimestamp, endTimestamp, step
  })
  return (
    <AnomalyCountChart
      {...props}
      chartData={chartData}
      labels={labels}
      isLoading={isLoading}
      message={message}
    />
  )
}

export default AnomalyCountChartWithData;
